/// <reference lib="dom" />
/**
 * Fancy weather overlay — filtr přes aktuální schéma, ne další theme.
 * Režim auto: WMO kód z geo.weather_now podle geolokace (jinak Praha).
 */
import { resolveSolarCoords } from './solar';
import {
  baselineWeatherMode,
  isAcmWeather,
  weatherFromWmo,
  type AcmWeather,
  type WeatherMode,
} from './weather-map';

export {
  baselineWeatherMode,
  isAcmWeather,
  weatherFromWmo,
  WEATHER_META,
  WEATHERS,
  type AcmWeather,
  type WeatherMode,
} from './weather-map';

const STORAGE_KEY = 'acm_weather';
const MODE_KEY = 'acm_weather_mode_v2';
const LEGACY_MODE_KEY = 'acm_weather_mode';
const AUTO_MS = 15 * 60_000;

function migrateWeatherDefault(): void {
  try {
    const decided = baselineWeatherMode(
      localStorage.getItem(MODE_KEY),
      localStorage.getItem(LEGACY_MODE_KEY),
    );
    if (!localStorage.getItem(MODE_KEY)) {
      if (decided.resetOverlay) {
        localStorage.removeItem(STORAGE_KEY);
        localStorage.removeItem(LEGACY_MODE_KEY);
      }
      localStorage.setItem(MODE_KEY, decided.mode);
    }
  } catch {
    /* private mode */
  }
}

export function readWeather(): AcmWeather {
  migrateWeatherDefault();
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (isAcmWeather(raw)) return raw;
  } catch {
    /* private mode */
  }
  return 'off';
}

export function readWeatherMode(): WeatherMode {
  migrateWeatherDefault();
  try {
    const m = localStorage.getItem(MODE_KEY);
    if (m === 'auto' || m === 'manual') return m;
  } catch {
    /* ignore */
  }
  return 'manual';
}

export function setWeatherMode(mode: WeatherMode): void {
  try {
    localStorage.setItem(MODE_KEY, mode);
  } catch {
    /* ignore */
  }
}

function emitWeather(weather: AcmWeather, mode: WeatherMode): void {
  try {
    window.dispatchEvent(
      new CustomEvent('acm-weather', { detail: { weather, mode } }),
    );
  } catch {
    /* ignore */
  }
}

/** Nastaví data-weather na <html> + localStorage. */
export function applyWeather(weather: AcmWeather): void {
  const root = document.documentElement;
  if (weather === 'off') {
    root.removeAttribute('data-weather');
  } else {
    root.setAttribute('data-weather', weather);
  }
  try {
    localStorage.setItem(STORAGE_KEY, weather);
  } catch {
    /* ignore */
  }
  emitWeather(weather, readWeatherMode());
}

export function applyManualWeather(weather: AcmWeather): void {
  stopWeatherAuto();
  setWeatherMode('manual');
  applyWeather(weather);
}

export async function fetchLiveWeather(): Promise<AcmWeather | null> {
  const { lat, lon } = await resolveSolarCoords();
  const { api } = await import('./api');
  const r = await api.mcpSession('geo.weather_now', { lat, lon }, { silent: true }) as {
    ok?: boolean;
    weather_code?: number;
    is_day?: number | boolean;
  };
  if (!r || r.ok === false || typeof r.weather_code !== 'number') return null;
  const isDay = r.is_day === true || r.is_day === 1;
  return weatherFromWmo(r.weather_code, isDay);
}

export async function refreshLiveWeather(): Promise<AcmWeather | null> {
  if (readWeatherMode() !== 'auto') return null;
  try {
    const next = await fetchLiveWeather();
    if (next == null || readWeatherMode() !== 'auto') return null;
    applyWeather(next);
    return next;
  } catch {
    return null;
  }
}

let autoTimer: ReturnType<typeof setInterval> | null = null;

export function startWeatherAuto(): void {
  setWeatherMode('auto');
  emitWeather(readWeather(), 'auto');
  void refreshLiveWeather();
  if (autoTimer != null) return;
  autoTimer = setInterval(() => {
    if (readWeatherMode() !== 'auto') return;
    void refreshLiveWeather();
  }, AUTO_MS);
}

export function stopWeatherAuto(): void {
  if (autoTimer == null) return;
  clearInterval(autoTimer);
  autoTimer = null;
}

/** Init před prvním paintem (volat z main.tsx). Auto počasí se nespouští. */
export function initWeather(): AcmWeather {
  const w = readWeather();
  if (w === 'off') {
    document.documentElement.removeAttribute('data-weather');
  } else {
    document.documentElement.setAttribute('data-weather', w);
  }
  return w;
}
